// Sidebar.jsx - WITH DEVELOPER ROLE SUPPORT
import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { message } from "antd";
import {
  DashboardOutlined,
  UserOutlined,
  TeamOutlined,
  CrownOutlined,
  QuestionCircleOutlined,
  TransactionOutlined,
  WalletOutlined,
  GiftOutlined,
  CommentOutlined,
  UsergroupAddOutlined,
  LogoutOutlined,
  HeartFilled,
  DownOutlined,
  RightOutlined,
  PlusOutlined,
  NotificationOutlined,
  BankOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import "./Sidebar.css";

function Sidebar({ isOpen, isMobile, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();

  // ✅ Get user role from localStorage
  const [userRole, setUserRole] = useState("");

  useEffect(() => {
    const role = localStorage.getItem("role") || "";
    setUserRole(role.toLowerCase());
    console.log("🎭 Current User Role:", role);
  }, []);

  const [expandedMenus, setExpandedMenus] = useState({
    premiumMembers: false,
  });

  const toggleMenu = (menuKey) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuKey]: !prev[menuKey]
    }));
  };

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    localStorage.clear();
    message.success("Logged out successfully");
    navigate("/Admin_Login");
  };

  // ✅ Check if user is developer
  const isDeveloper = userRole === "developer" || userRole === "dev";

  // ✅ Pages to hide for developer
  const hiddenForDeveloper = [
    "/admin/withdrawals",
    // Add more paths here if needed later
  ];

  const shouldHideItem = (path) => {
    if (isDeveloper && hiddenForDeveloper.includes(path)) {
      return true;
    }
    return false;
  };

  const menuSections = [
    {
      title: null,
      items: [
        {
          icon: <DashboardOutlined />,
          label: "Dashboard",
          path: "/Admin_Panel",
        },
      ],
    },
    {
      title: "MANAGEMENT",
      items: [
        { icon: <UserOutlined />, label: "Users", path: "/admin/users" },
        { icon: <TeamOutlined />, label: "Staff", path: "/admin/staff" },
        {
          icon: <CrownOutlined />,
          label: "Premium Members",
          isSubMenu: true,
          menuKey: "premiumMembers",
          subItems: [
            { icon: <CrownOutlined />, label: "Premium Members List", path: "/premium-members" },
            { icon: <PlusOutlined />, label: "Add Premium Plan", path: "/admin/add-premium-plan" },
          ],
        },
        { icon: <QuestionCircleOutlined />, label: "Contact Enquiries", path: "/admin/contactenquiries" },
        { icon: <HeartFilled />, label: "Success Stories", path: "/admin/success-stories" },
        { icon: <NotificationOutlined />, label: "Promotions", path: "/admin/promotions" },
        { icon: <CommentOutlined />, label: "Reports", path: "/reports" },
      ],
    },
    {
      title: "FINANCIAL",
      items: [
        { icon: <TransactionOutlined />, label: "Transactions", path: "/admin/transactions" },
        { icon: <WalletOutlined />, label: "Withdrawals", path: "/admin/withdrawals" },
        { icon: <WalletOutlined />, label: "Wallet Management", path: "/admin/wallet" },
        { icon: <GiftOutlined />, label: "Commission", path: "/admin/commission" },
        // ✅ Developer only menus
        ...(isDeveloper ? [
          { icon: <WalletOutlined />, label: "My Withdrawals", path: "/developer/withdrawals" },
          { icon: <BankOutlined />, label: "Bank Details", path: "/developer/bank-details" },
        ] : []),
      ],
    },
    {
      title: "SETTINGS",
      items: [
        { icon: <UsergroupAddOutlined />, label: "Admin Users", path: "/admin-users" },
        { icon: <GlobalOutlined />, label: "Religion Settings", path: "/admin/religion-settings" },
      ],
    },
  ];

  const renderMenuItem = (item, idx) => {
    if (!item) return null;

    // ✅ Hide if in hidden list
    if (!item.isSubMenu && shouldHideItem(item.path)) {
      return null;
    }

    if (item.isSubMenu) {
      const isExpanded = expandedMenus[item.menuKey];
      const subItems = (item.subItems || []).filter(sub =>
        sub && !shouldHideItem(sub.path)
      );

      if (subItems.length === 0) return null;

      const hasActiveChild = subItems.some(sub => sub && isActive(sub.path));

      return (
        <li key={item.menuKey || `submenu-${idx}`} className="sidebar-menu-item-wrapper">
          <div
            className={`sidebar-menu-item ${hasActiveChild ? 'active-parent' : ''}`}
            onClick={() => toggleMenu(item.menuKey)}
            style={{ cursor: 'pointer' }}
          >
            <span className="sidebar-icon">{item.icon}</span>
            <span className="sidebar-label">{item.label}</span>
            <span className="sidebar-arrow">
              {isExpanded ? <DownOutlined /> : <RightOutlined />}
            </span>
          </div>

          {isExpanded && subItems.length > 0 && (
            <ul className="sidebar-submenu">
              {subItems.map((subItem, subIdx) => (
                <li key={subItem.path || `sub-${subIdx}`}>
                  <Link
                    to={subItem.path}
                    className={`sidebar-menu-item sidebar-submenu-item ${isActive(subItem.path) ? "active" : ""
                      }`}
                    onClick={() => isMobile && onClose && onClose()}
                  >
                    <span className="sidebar-icon">{subItem.icon}</span>
                    <span className="sidebar-label">{subItem.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </li>
      );
    }

    return (
      <li key={item.path || `item-${idx}`}>
        <Link
          to={item.path}
          className={`sidebar-menu-item ${isActive(item.path) ? "active" : ""}`}
          onClick={() => isMobile && onClose && onClose()}
        >
          <span className="sidebar-icon">{item.icon}</span>
          <span className="sidebar-label">{item.label}</span>
        </Link>
      </li>
    );
  };

  return (
    <aside className={`admin-sidebar ${isOpen ? "open" : "closed"}`}>
      {/* Logo Section */}
      <div className="sidebar-logo-section">
        <div className="sidebar-logo-icon">
          <HeartFilled />
        </div>
        <div className="sidebar-logo-text">
          <h2>Matrimonial</h2>
          <p>{isDeveloper ? "Developer Panel" : "Admin Panel"}</p>
        </div>
      </div>

      {/* ✅ Developer Badge */}
      {isDeveloper && (
        <div style={{
          background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
          color: '#fff',
          padding: '8px 16px',
          margin: '10px 16px',
          borderRadius: '10px',
          fontSize: '11px',
          fontWeight: 700,
          textAlign: 'center',
          letterSpacing: '0.5px',
          boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)'
        }}>
          💻 DEVELOPER MODE
        </div>
      )}

      {/* Menu Items */}
      <nav className="sidebar-nav">
        {menuSections.map((section, sectionIdx) => {
          const visibleItems = (section.items || []).filter(item => {
            if (!item) return false;
            if (item.isSubMenu) {
              const visibleSubs = (item.subItems || []).filter(sub =>
                sub && !shouldHideItem(sub.path)
              );
              return visibleSubs.length > 0;
            }
            return !shouldHideItem(item.path);
          });

          if (visibleItems.length === 0) return null;

          return (
            <div key={sectionIdx} className="sidebar-section">
              {section.title && (
                <div className="sidebar-section-title">{section.title}</div>
              )}
              <ul className="sidebar-menu">
                {visibleItems.map((item, idx) => renderMenuItem(item, idx))}
              </ul>
            </div>
          );
        })}
      </nav>

      {/* Logout Button */}
      <div className="sidebar-logout-section">
        <button className="sidebar-logout-btn" onClick={handleLogout}>
          <LogoutOutlined />
          <span>Logout</span>
        </button>
      </div>
    </aside>
  );
}

export default Sidebar;